import Toastify from 'toastify-js'
import filterHelper from '@u/helpers/filterHelper'
import searchListHelper from '@u/searchListHelper'
import settingsHelpers from '@u/helpers/settingsHelpers'
import stringHelpers from '@u/helpers/stringHelpers'
import dateHelpers from '@u/helpers/dateHelpers'
import invoiceComponentHelpers from '@u/helpers/invoiceComponentHelpers'
import store from '@/store'

import moment from 'moment'
import 'moment/dist/locale/fr-ch'
import 'moment/dist/locale/nl-be'
import 'moment/dist/locale/nl'
import 'moment/dist/locale/es'
import 'moment/dist/locale/fr'
import 'moment/dist/locale/ro'
import { devApiPort } from '@u/http/apiOrigin'
import { contentDispositionFilename } from '@u/http/contentDispositionFilename'
import PartnersLogo from '@/assets/enums/config/PartnersLogo'

const helpers = {
  ...filterHelper,
  ...searchListHelper,
  ...settingsHelpers,
  ...stringHelpers,
  ...dateHelpers,
  ...invoiceComponentHelpers,
  /**
   * Return the base64 from File or Blob
   * @param file {File | Blob}
   * @return {Promise<string>}
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)

      reader.readAsDataURL(file)
    })
  },
  /**
   * @param data
   * @param name
   * @param formData
   * @return {FormData}
   */
  toFormData(data, name = '', formData = null) {
    if (!formData) {
      formData = new FormData()
    }

    for (const keyName in data) {
      const value = data[keyName]
      const formDataName = name ? `${name}[${keyName}]` : keyName

      if (value instanceof File) {
        formData.append(formDataName, value)
      } else if (
        value &&
        (value instanceof Array || typeof value === 'object')
      ) {
        this.toFormData(value, formDataName, formData)
      } else {
        formData.append(formDataName, value)
      }
    }

    return formData
  },
  setTheme(theme = null) {
    if (theme !== null) {
      document.documentElement.setAttribute('data-theme', theme)
      localStorage.setItem('theme', theme)
    } else {
      localStorage.removeItem('theme')
    }
  },
  getTheme() {
    return !!localStorage.getItem('theme')
  },
  setPattern(pattern = null) {
    if (pattern !== null) {
      document.documentElement.setAttribute('data-pattern', pattern)
      localStorage.setItem('pattern', pattern)
    } else {
      document.documentElement.removeAttribute('data-pattern')
      localStorage.removeItem('pattern')
    }
  },
  getPattern() {
    return localStorage.getItem('pattern')
  },
  setPatternIntensity(intensity = null) {
    if (intensity !== null) {
      document.documentElement.setAttribute('data-pattern-intensity', intensity)
      localStorage.setItem('patternIntensity', intensity)
    } else {
      document.documentElement.removeAttribute('data-pattern-intensity')
      localStorage.removeItem('patternIntensity')
    }
  },
  getPatternIntensity() {
    return localStorage.getItem('patternIntensity')
  },
  setAvatarShape(shape = null) {
    if (shape !== null) {
      document.documentElement.setAttribute('data-avatar-shape', shape)
      localStorage.setItem('avatarShape', shape)
    } else {
      document.documentElement.removeAttribute('data-avatar-shape')
      localStorage.removeItem('avatarShape')
    }
  },
  getAvatarShape() {
    return localStorage.getItem('avatarShape')
  },
  setScrollbarVisibility(visibility = null) {
    if (visibility !== null) {
      document.documentElement.setAttribute(
        'data-scrollbar-visibility',
        visibility
      )
      localStorage.setItem('scrollbarVisibility', visibility)
    } else {
      document.documentElement.removeAttribute('data-scrollbar-visibility')
      localStorage.removeItem('scrollbarVisibility')
    }
  },
  getScrollbarVisibility() {
    return localStorage.getItem('scrollbarVisibility')
  },
  setScrollbarWidth(width = null) {
    if (width !== null) {
      document.documentElement.setAttribute('data-scrollbar-width', width)
      localStorage.setItem('scrollbarWidth', width)
    } else {
      document.documentElement.removeAttribute('data-scrollbar-width')
      localStorage.removeItem('scrollbarWidth')
    }
  },
  getScrollbarWidth() {
    return localStorage.getItem('scrollbarWidth')
  },
  initPattern() {
    const pattern = localStorage.getItem('pattern')
    if (pattern) {
      this.setPattern(pattern)
    }
    const intensity = localStorage.getItem('patternIntensity')
    if (intensity) {
      this.setPatternIntensity(intensity)
    }
  },
  initScrollbar() {
    const visibility = localStorage.getItem('scrollbarVisibility')
    if (visibility) {
      this.setScrollbarVisibility(visibility)
    }
    const width = localStorage.getItem('scrollbarWidth')
    if (width) {
      this.setScrollbarWidth(width)
    }
  },
  getDevPort() {
    return devApiPort()
  },
  initTheme() {
    let theme = localStorage.getItem('theme')
    if (theme !== null) {
      if (theme.substring(0, 6) === 'theme-') {
        theme = theme.substring(7)
      }
      this.setTheme(theme)
      return this.setTheme(theme)
    }
    if (document.body.getAttribute('data-brand') === 'funero') {
      this.setTheme('tangerine')
      return this.setTheme('tangerine')
    } else {
      this.setTheme('blue')
      return this.setTheme('blue')
    }
  },
  initBrand(app) {
    const { hostname } = window.location
    const domain = hostname.split('.')[1]
    const subdomain = hostname.split('.')[0]
    let brand = 'funeral-manager'
    if (hostname.includes('funecare')) {
      brand = 'funecare'
    } else if (['ginkgho', 'ginkgho-test', 'funerali'].includes(subdomain)) {
      brand = 'ginkgho'
    } else if (domain === 'funero') {
      brand = 'funero'
    }
    document.body.setAttribute('data-brand', brand)
    if (app) {
      app.config.globalProperties.$brand = brand
    }
    this.initTheme()
    this.initPattern()
    this.initScrollbar()
  },
  toastify(text, param = {}) {
    Toastify({
      text: text,
      duration: 3000,
      newWindow: true,
      close: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      className: 'toastify-settings',
      ...param
    }).showToast()
  },
  toastifyHtml(param = {}) {
    Toastify({
      duration: 3000,
      newWindow: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      ...param
    }).showToast()
  },
  getToastCloneElement(id) {
    let notificationHtmlElement = document.getElementById(id).cloneNode(true)
    notificationHtmlElement.removeAttribute('id')
    notificationHtmlElement.classList.remove('hidden')
    return notificationHtmlElement
  },
  toastAction(type, title, description, actions, _icon) {
    let toastElement = this.getToastCloneElement('toast_action_content')
    toastElement.querySelector('.toast-title').innerHTML = title
    if (!description) {
      toastElement.querySelector('.toast-description').remove()
    } else {
      toastElement.querySelector('.toast-description').innerHTML = description
    }
    if (['info', 'delete'].includes(type)) {
      toastElement
        .querySelector(`.toast-icon-${type}`)
        .classList.remove('!hidden')
    }
    if (Array.isArray(actions)) {
      let ctaContainer = toastElement.querySelector('.toast-cta-container')
      let actionButton = toastElement
        .querySelector('.template-button')
        .cloneNode(true)
      toastElement.querySelector('.template-button').remove()
      actions.forEach((action) => {
        let newButton = actionButton.cloneNode(true)
        newButton.classList.remove('hidden', 'template-button')
        newButton.innerText = action.title
        if (action.class) {
          newButton.classList.add(action.class)
        }
        if (action.action && typeof action.action === 'function') {
          newButton.onclick = () => {
            action.action()
            toastElement.remove()
          }
        }
        ctaContainer.append(newButton)
      })
    }
    Toastify({
      node: toastElement,
      duration: 5000,
      newWindow: false,
      close: false,
      gravity: 'top',
      position: 'right',
      stopOnFocus: false
    }).showToast()
  },
  /**
   * `partnerLogo` is floated right of the title instead of the type icon, so the user sees at a glance which partner the message comes from.
   */
  toast(type, title, text, uniqId, partnerLogo = null) {
    if (uniqId !== undefined && !!document.getElementById(uniqId)) {
      return
    }
    let notificationHtmlElement = document
      .getElementById('generic_toast')
      .cloneNode(true)
    notificationHtmlElement.removeAttribute('id')
    if (uniqId !== undefined) {
      notificationHtmlElement.setAttribute('id', uniqId)
    }
    notificationHtmlElement.classList.remove('!hidden')
    notificationHtmlElement.getElementsByClassName('toast-title')[0].innerHTML =
      title
    if (text && text.length) {
      let descriptionElement =
        notificationHtmlElement.getElementsByClassName('toast-description')[0]
      descriptionElement.innerHTML = text
      descriptionElement.classList.remove('!hidden')
    }
    if (partnerLogo) {
      const logoElement =
        notificationHtmlElement.getElementsByClassName('toast-partner-logo')[0]
      logoElement.setAttribute('src', partnerLogo)
      logoElement.classList.remove('!hidden')
      // Logo floated right of the title, the description wraps around it.
      notificationHtmlElement
        .getElementsByClassName('toast-icon-container')[0]
        .classList.add('!hidden')
      notificationHtmlElement
        .getElementsByClassName('toast-content')[0]
        .classList.remove('ml-3')
    } else {
      notificationHtmlElement
        .getElementsByClassName(`toast-icon-${type}`)[0]
        .classList.remove('!hidden')
    }

    const instance = Toastify({
      node: notificationHtmlElement,
      // duration: type === 'success' ? 2000 : 5000,
      duration: 7000,
      newWindow: false,
      close: false,
      gravity: 'top',
      position: 'right',
      stopOnFocus: false,
      onClick: () => instance.hideToast()
    })

    instance.showToast()
  },
  toastSuccess: (title, text, uniqId) => {
    helpers.toast('success', title, text, uniqId)
  },
  toastWarning: (title, text, uniqId) => {
    helpers.toast('warning', title, text, uniqId)
  },
  toastDanger: (title, text, uniqId) => {
    helpers.toast('danger', title, text, uniqId)
  },
  toastInfo: (title, text, uniqId) => {
    helpers.toast('info', title, text, uniqId)
  },
  /**
   * Error raised by an external partner (`{ partner, message, detail, details }`
   * from the API): shown with the partner's logo so it is clear the problem is on their side.
   */
  toastPartnerError(error) {
    const lines = [error.detail, ...(error.details ?? [])]
      .filter((line) => typeof line === 'string' && line.length)
      .filter((line) => line !== error.message)
      .map((line) => helpers.escapeHtml(line))

    helpers.toast(
      'warning',
      helpers.escapeHtml(error.message ?? ''),
      lines.join('<br>'),
      undefined,
      PartnersLogo[error.partner] ?? null
    )
  },
  unsavedChangesNotification(next) {
    let notificationHtmlElement = document
      .getElementById('unsaved_change_notification')
      .cloneNode(true)
    notificationHtmlElement.removeAttribute('id')
    notificationHtmlElement.classList.remove('hidden')

    notificationHtmlElement
      .getElementsByClassName('btn_stay_page')[0]
      .addEventListener('click', () => {
        notificationHtmlElement.classList.add('hidden')
      })
    notificationHtmlElement
      .getElementsByClassName('btn_next_page')[0]
      .addEventListener('click', () => {
        notificationHtmlElement.classList.add('hidden')
        next()
      })

    Toastify({
      node: notificationHtmlElement,
      duration: 5000,
      newWindow: false,
      close: false,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      onClick() {
        this.hideToast()
      }
    }).showToast()
  },
  cutText(text, length) {
    if (text.split(' ').length > 1) {
      const string = text.substring(0, length)
      const splitText = string.split(' ')
      splitText.pop()
      return splitText.join(' ') + '...'
    } else {
      return text
    }
  },
  formatDate(date, format, locale) {
    locale = locale !== undefined ? locale : 'fr'
    let _moment = moment(date)
    return _moment.isValid() ? _moment.locale(locale).format(format) : ''
  },
  momentLocalised(date, format, locale) {
    if (locale === undefined) {
      switch (store.getters['i18n/getLocal']) {
        case 'fr_BE':
        case 'fr_FR':
          locale = 'fr'
          break
        case 'fr_CH':
          locale = 'fr-ch'
          break
        case 'nl_BE':
          locale = 'nl-be'
          break
        case 'nl_NL':
          locale = 'nl'
          break
        default:
          locale = 'en'
      }
    }
    return moment(date).locale(locale).format(format)
  },
  capitalizeFirstLetter(string) {
    if (string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    } else {
      return ''
    }
  },
  onlyNumber(string) {
    if (string) {
      return string.replace(/\D/g, '')
    } else {
      return ''
    }
  },
  formatCurrency(price, currency = 'EUR', locale = null) {
    const htmlLang = document.documentElement.lang
    const activeLocale =
      locale ||
      (htmlLang && htmlLang !== 'default'
        ? htmlLang.replace('_', '-')
        : 'fr-BE')
    return Number(price).toLocaleString(activeLocale, {
      style: 'currency',
      currency: currency
    })
  },
  isset(obj) {
    if (obj !== null && obj !== undefined) {
      if (typeof obj === 'object' || Array.isArray(obj)) {
        return Object.keys(obj).length
      } else {
        return obj.toString().length
      }
    }

    return false
  },
  toRaw(obj) {
    return JSON.parse(JSON.stringify(obj))
  },
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj))
  },
  randomNumbers(from, to, length) {
    const numbers = [0]
    for (let i = 1; i < length; i++) {
      numbers.push(Math.ceil(Math.random() * (from - to) + to))
    }

    return numbers
  },
  pluck(object, key) {
    return object.map((company) => company[key])
  },
  getTranslatedText(obj) {
    let local = store.getters['i18n/getLocal']
    if (obj[local]) return obj[local]
    if (obj['fr_BE']) return obj['fr_BE']
    return obj
  },
  downloadURI: (uri, name) => {
    const link = document.createElement('a')
    link.download = name
    link.href = uri
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },
  filenameFromContentDisposition: contentDispositionFilename,
  toggleElementInArray(array, element) {
    if (array.indexOf(element) !== -1) {
      array.splice(array.indexOf(element), 1)
    } else {
      array.push(element)
    }
    return array
  },
  getLocalizedLabel(label, locale, fallback = '') {
    if (!label || typeof label !== 'object') return label ?? fallback
    return (
      label[locale] ??
      label[locale.replace('-', '_')] ??
      label[locale.replace('_', '-')] ??
      Object.values(label)[0] ??
      fallback
    )
  }
}

const install = (app) => {
  app.config.globalProperties.$h = helpers
}

export { install as default, helpers as helper }
